import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useAuth } from "@/context/AuthContext";
import { useT } from "@/context/I18nContext";
import RolePermissionsManager from "@/components/RolePermissionsManager";
import CreateUserDialog from "@/components/CreateUserDialog";
import RegionsManager from "@/components/RegionsManager";
import { toast } from "sonner";
import { Switch } from "@/components/ui/switch";
import { UserCheck, UserX, ShieldCheck, Archive, Trash2, ArchiveRestore, Globe, MapPin, Save, GripVertical, Plus } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

const ROLES = [
  "developer", "national_admin", "regional_admin", "chapter_leader",
  "scout_leader", "cubs_leader", "patrol_leader", "patrol_co_leader",
  "parent", "scout", "consultant",
];

const ROLE_LABEL = {
  scout: "Scout", parent: "Parent",
  patrol_co_leader: "Patrol Co-Leader", patrol_leader: "Patrol Leader",
  cubs_leader: "Cubs Leader", scout_leader: "Scout Leader",
  chapter_leader: "Chapter Leader", regional_admin: "Regional Admin",
  national_admin: "National Admin",
  developer: "Developer",
  consultant: "Consultant",
};

export default function Administration() {
  const { user } = useAuth();
  const { t } = useT();
  const [users, setUsers] = useState([]);
  const [pending, setPending] = useState([]);
  const [chapters, setChapters] = useState([]);
  const [regions, setRegions] = useState([]);
  const [logs, setLogs] = useState([]);
  const [includeScouts, setIncludeScouts] = useState(false);
  const [showArchived, setShowArchived] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);

  const loadUsers = () => {
    const params = new URLSearchParams();
    if (includeScouts) params.set("include_scouts", "true");
    if (showArchived) params.set("status", "archived");
    api.get(`/users?${params}`).then(r => setUsers(r.data));
  };
  const loadPending = () => api.get("/users/pending").then(r => setPending(r.data));

  useEffect(() => {
    loadUsers();
    /* eslint-disable-next-line */
  }, [includeScouts, showArchived]);
  useEffect(() => {
    loadPending();
    api.get("/chapters").then(r => setChapters(r.data));
    api.get("/regions").then(r => setRegions(r.data));
    if (["national_admin","developer"].includes(user?.role)) api.get("/audit-logs").then(r => setLogs(r.data));
  }, []);

  const setRole = async (uid, role, chapter_id, region_id) => {
    try { await api.put(`/users/${uid}/role`, { role, chapter_id, region_id }); toast.success("Updated"); loadUsers(); }
    catch { toast.error("Failed"); }
  };
  const approve = async (uid) => {
    try { await api.post(`/users/${uid}/approve`); toast.success("Approved"); loadPending(); loadUsers(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const reject = async (uid) => {
    try { await api.post(`/users/${uid}/reject`); toast.success("Rejected"); loadPending(); }
    catch { toast.error("Failed"); }
  };
  const archive = async (uid) => {
    if (!window.confirm("Archive this user? They won't be able to sign in.")) return;
    try { await api.post(`/users/${uid}/archive`); toast.success("Archived"); loadUsers(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const unarchive = async (uid) => {
    try { await api.post(`/users/${uid}/unarchive`); toast.success("Restored"); loadUsers(); }
    catch { toast.error("Failed"); }
  };
  const purge = async (uid) => {
    if (!window.confirm("Permanently delete this user? This cannot be undone.")) return;
    try { await api.delete(`/users/${uid}`); toast.success("Deleted"); loadUsers(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="space-y-6">
      <div>
        <div className="uppercase-label">{t("Command Center", "Հրամանատարական կենտրոն")}</div>
        <h1 className="font-display text-4xl lg:text-5xl font-black tracking-tight mt-1">{t("Administration", "Կառավարում")}</h1>
      </div>

      <Tabs defaultValue={pending.length ? "pending" : "users"}>
        <TabsList className="rounded-full bg-muted p-1 flex-wrap h-auto">
          <TabsTrigger value="pending" className="rounded-full" data-testid="tab-pending">
            Pending Approvals {pending.length > 0 && <Badge className="ml-2 rounded-full bg-[hsl(12,65%,63%)]">{pending.length}</Badge>}
          </TabsTrigger>
          <TabsTrigger value="users" className="rounded-full" data-testid="tab-users">Users</TabsTrigger>
          {["national_admin","developer"].includes(user?.role) && <TabsTrigger value="audit" className="rounded-full" data-testid="tab-audit">Audit Log</TabsTrigger>}
          {["national_admin","developer"].includes(user?.role) && <TabsTrigger value="homepage" className="rounded-full" data-testid="tab-homepage">Homepage</TabsTrigger>}
          {["national_admin","developer"].includes(user?.role) && <TabsTrigger value="translations" className="rounded-full" data-testid="tab-translations">Translations</TabsTrigger>}
          {["national_admin","developer"].includes(user?.role) && <TabsTrigger value="vocab" className="rounded-full" data-testid="tab-vocab">Vocabulary</TabsTrigger>}
          {["national_admin","developer"].includes(user?.role) && <TabsTrigger value="roles" className="rounded-full" data-testid="tab-roles">Roles</TabsTrigger>}
          {["national_admin","developer"].includes(user?.role) && <TabsTrigger value="regions" className="rounded-full" data-testid="tab-regions">{t("Regions", "Տարածաշրջաններ")}</TabsTrigger>}
        </TabsList>

        <TabsContent value="pending">
          <Card className="clay-card p-0 overflow-hidden mt-4">
            {pending.length === 0 ? (
              <div className="p-10 text-center text-sm text-muted-foreground">
                <ShieldCheck size={40} className="mx-auto mb-3 opacity-30"/>
                All caught up — no accounts awaiting approval.
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Applicant</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Chapter</TableHead>
                    <TableHead>Requested role</TableHead>
                    <TableHead>Submitted</TableHead>
                    <TableHead className="text-right">Action</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {pending.map((u, i) => (
                    <TableRow key={u.user_id} className={i % 2 ? "bg-muted/30" : ""} data-testid={`pending-row-${u.user_id}`}>
                      <TableCell>
                        <div className="font-semibold">{u.name}</div>
                        <div className="text-xs text-muted-foreground">{u.email}</div>
                      </TableCell>
                      <TableCell>
                        <Badge className={`rounded-full ${u.signup_type === "leader" ? "bg-[hsl(12,65%,63%)]" : "bg-[hsl(149,40%,30%)]"}`}>
                          {u.signup_type === "leader" ? "Leader" : "Scout"}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-sm">{chapters.find(c => c.chapter_id === u.chapter_id)?.name || u.chapter_id}</TableCell>
                      <TableCell><Badge variant="outline" className="rounded-full">{ROLE_LABEL[u.requested_role] || u.requested_role || "Scout"}</Badge></TableCell>
                      <TableCell className="text-xs">{new Date(u.created_at).toLocaleDateString()}</TableCell>
                      <TableCell className="text-right whitespace-nowrap">
                        <Button size="sm" className="btn-pill bg-[hsl(149,40%,30%)] hover:bg-[hsl(149,45%,25%)] mr-1" onClick={() => approve(u.user_id)} data-testid={`approve-${u.user_id}`}>
                          <UserCheck size={14} className="mr-1"/> Approve
                        </Button>
                        <Button size="sm" variant="outline" className="btn-pill text-[hsl(0,65%,55%)] border-[hsl(0,65%,55%)]/40 hover:bg-[hsl(0,65%,55%)]/10" onClick={() => reject(u.user_id)} data-testid={`reject-${u.user_id}`}>
                          <UserX size={14} className="mr-1"/> Reject
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </Card>
        </TabsContent>

        <TabsContent value="users">
          <div className="flex items-center gap-6 mt-4 mb-2 flex-wrap">
            <label className="flex items-center gap-2 text-xs uppercase tracking-widest font-bold cursor-pointer">
              <Switch checked={includeScouts} onCheckedChange={setIncludeScouts} data-testid="users-include-scouts"/>
              Include scouts
            </label>
            <label className="flex items-center gap-2 text-xs uppercase tracking-widest font-bold cursor-pointer">
              <Switch checked={showArchived} onCheckedChange={setShowArchived} data-testid="users-show-archived"/>
              Show archived
            </label>
            <span className="text-xs text-muted-foreground flex-1 min-w-[200px]">Scouts live in the Members database — Administration shows leaders & admins by default.</span>
            {["national_admin","developer","regional_admin"].includes(user?.role) && (
              <Button className="btn-pill bg-[hsl(12,65%,63%)] hover:bg-[hsl(12,70%,55%)]" onClick={() => setCreateOpen(true)} data-testid="new-user-btn">
                <Plus size={14} className="mr-2"/>Create user
              </Button>
            )}
          </div>
          <Card className="clay-card p-0 overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead><TableHead>Email</TableHead>
                  <TableHead>Role</TableHead><TableHead>Chapter</TableHead><TableHead>{t("Region", "Տարածաշրջան")}</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {users.map((u, i) => (
                  <TableRow key={u.user_id} className={i % 2 ? "bg-muted/30" : ""} data-testid={`user-row-${u.user_id}`}>
                    <TableCell className="font-semibold">{u.name}</TableCell>
                    <TableCell className="text-sm">{u.email}</TableCell>
                    <TableCell>
                      {["national_admin","developer"].includes(user?.role) ? (
                        <Select value={u.role} onValueChange={(v) => setRole(u.user_id, v, u.chapter_id, v === "regional_admin" ? (u.region_id || regions[0]?.region_id) : u.region_id)}>
                          <SelectTrigger className="w-52 h-8"><SelectValue/></SelectTrigger>
                          <SelectContent>{ROLES.map(r => <SelectItem key={r} value={r}>{ROLE_LABEL[r] || r}</SelectItem>)}</SelectContent>
                        </Select>
                      ) : <Badge variant="outline" className="rounded-full">{ROLE_LABEL[u.role] || u.role}</Badge>}
                    </TableCell>
                    <TableCell>
                      {["national_admin","developer"].includes(user?.role) ? (
                        <Select value={u.chapter_id || "none"} onValueChange={(v) => setRole(u.user_id, u.role, v === "none" ? null : v, u.region_id)}>
                          <SelectTrigger className="w-44 h-8"><SelectValue/></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">— none —</SelectItem>
                            {chapters.map(c => <SelectItem key={c.chapter_id} value={c.chapter_id}>{c.name}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      ) : <span className="text-sm">{chapters.find(c => c.chapter_id === u.chapter_id)?.name || "—"}</span>}
                    </TableCell>
                    <TableCell>
                      {u.role === "regional_admin" && ["national_admin","developer"].includes(user?.role) ? (
                        <Select value={u.region_id || "none"} onValueChange={(v) => setRole(u.user_id, u.role, u.chapter_id, v === "none" ? "" : v)}>
                          <SelectTrigger className="w-36 h-8" data-testid={`user-region-${u.user_id}`}><SelectValue/></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">— none —</SelectItem>
                            {regions.map(r => <SelectItem key={r.region_id} value={r.region_id}>{r.name}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      ) : <span className="text-sm">{u.role === "regional_admin" ? (regions.find(r => r.region_id === u.region_id)?.name || "—") : ""}</span>}
                    </TableCell>
                    <TableCell>
                      <Badge variant={u.status === "active" ? "default" : "secondary"} className="rounded-full text-xs">
                        {u.status || "active"}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right whitespace-nowrap">
                      {u.status === "archived" ? (
                        <Button size="sm" variant="ghost" onClick={() => unarchive(u.user_id)} data-testid={`unarchive-${u.user_id}`}>
                          <ArchiveRestore size={14} className="mr-1"/> Restore
                        </Button>
                      ) : (
                        <Button size="sm" variant="ghost" onClick={() => archive(u.user_id)} data-testid={`archive-user-${u.user_id}`}>
                          <Archive size={14}/>
                        </Button>
                      )}
                      {["national_admin","developer"].includes(user?.role) && (
                        <Button size="sm" variant="ghost" onClick={() => purge(u.user_id)} className="text-[hsl(0,65%,55%)] hover:bg-[hsl(0,65%,55%)]/10" data-testid={`delete-user-${u.user_id}`}>
                          <Trash2 size={14}/>
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
        </TabsContent>

        {["national_admin","developer"].includes(user?.role) && (
          <TabsContent value="audit">
            <Card className="clay-card p-0 overflow-hidden mt-4">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>When</TableHead><TableHead>User</TableHead>
                    <TableHead>Action</TableHead><TableHead>Entity</TableHead><TableHead>Details</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {logs.map((l, i) => (
                    <TableRow key={l.log_id} className={i % 2 ? "bg-muted/30" : ""}>
                      <TableCell className="text-xs">{new Date(l.created_at).toLocaleString()}</TableCell>
                      <TableCell className="text-sm">{l.user_email}</TableCell>
                      <TableCell><Badge variant="outline" className="rounded-full text-xs">{l.action}</Badge></TableCell>
                      <TableCell className="text-sm">{l.entity}</TableCell>
                      <TableCell className="text-xs text-muted-foreground truncate max-w-xs">{JSON.stringify(l.meta)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Card>
          </TabsContent>
        )}

        {["national_admin","developer"].includes(user?.role) && (
          <TabsContent value="homepage">
            <HomepageSettings/>
          </TabsContent>
        )}

        {["national_admin","developer"].includes(user?.role) && (
          <>
            <TabsContent value="translations">
              <TranslationsManager/>
            </TabsContent>

            <TabsContent value="vocab">
              <VocabularyManager/>
            </TabsContent>

            <TabsContent value="roles">
              <RolePermissionsManager/>
            </TabsContent>

            <TabsContent value="regions">
              <RegionsManager/>
            </TabsContent>
          </>
        )}
      </Tabs>

      {["national_admin","developer","regional_admin"].includes(user?.role) && (
        <CreateUserDialog
          open={createOpen}
          onClose={() => setCreateOpen(false)}
          onCreated={() => { loadUsers(); loadPending(); }}
          chapters={chapters}
          regions={regions}
          currentUser={user}
        />
      )}
    </div>
  );
}

function TranslationsManager() {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [dirty, setDirty] = useState({});
  const [saving, setSaving] = useState(false);
  const [newEn, setNewEn] = useState("");
  const [newHy, setNewHy] = useState("");

  const load = () => api.get("/translations").then(r => { setItems(r.data); setDirty({}); }).catch(() => {});
  useEffect(() => { load(); }, []);

  const setHy = (en, hy) => setDirty(prev => ({ ...prev, [en]: hy }));

  const notifyUpdated = () => {
    try { window.dispatchEvent(new CustomEvent("translations-updated")); } catch (err) { /* noop */ }
  };

  const saveAll = async () => {
    const entries = Object.entries(dirty).map(([en, hy]) => ({ en, hy }));
    if (!entries.length) return toast("Nothing to save");
    setSaving(true);
    try {
      await api.put("/translations", { entries });
      toast.success(`Saved ${entries.length} translation${entries.length === 1 ? "" : "s"}`);
      load(); notifyUpdated();
    } catch { toast.error("Save failed"); }
    finally { setSaving(false); }
  };

  const addNew = async () => {
    if (!newEn.trim()) return toast.error("Enter the English text first");
    try {
      await api.put("/translations", { entries: [{ en: newEn.trim(), hy: newHy.trim() }] });
      toast.success("Added");
      setNewEn(""); setNewHy(""); load(); notifyUpdated();
    } catch { toast.error("Failed"); }
  };

  const remove = async (en) => {
    if (!window.confirm(`Delete translation for “${en}”?`)) return;
    try { await api.delete(`/translations?en=${encodeURIComponent(en)}`); toast.success("Deleted"); load(); notifyUpdated(); }
    catch { toast.error("Failed"); }
  };

  const filtered = items.filter(it => {
    if (!q.trim()) return true;
    const s = q.toLowerCase();
    return (it.en || "").toLowerCase().includes(s) || (it.hy || "").toLowerCase().includes(s);
  });

  const untranslated = filtered.filter(it => !it.hy).length;
  const dirtyCount = Object.keys(dirty).length;

  return (
    <div className="space-y-4 mt-4">
      <Card className="clay-card p-6" data-testid="translations-add-card">
        <div className="flex items-center gap-2 mb-4">
          <Globe size={16} className="text-[hsl(12,65%,55%)]"/>
          <h3 className="font-display font-bold text-lg">Add or update a phrase</h3>
        </div>
        <div className="grid md:grid-cols-2 gap-3">
          <div>
            <Label>English (source key)</Label>
            <Input value={newEn} onChange={e => setNewEn(e.target.value)} placeholder="e.g. Prepared. Together. Outdoors." data-testid="tr-new-en"/>
          </div>
          <div>
            <Label>Armenian</Label>
            <Input value={newHy} onChange={e => setNewHy(e.target.value)} placeholder="օրինակ՝ Պատրաստ։ Միասին։ Բնության մեջ։" data-testid="tr-new-hy"/>
          </div>
        </div>
        <div className="flex justify-end mt-3">
          <Button onClick={addNew} className="btn-pill bg-[hsl(149,40%,30%)]" data-testid="tr-add-btn">
            <Save size={14} className="mr-2"/> Save phrase
          </Button>
        </div>
        <p className="text-xs text-muted-foreground mt-3">
          Any phrase saved here immediately overrides the built-in Armenian text on the homepage. Match the exact English wording (case + punctuation) so the site can find it.
        </p>
      </Card>

      <Card className="clay-card p-6" data-testid="translations-list-card">
        <div className="flex items-center justify-between flex-wrap gap-2 mb-4">
          <div>
            <h3 className="font-display font-bold text-lg">Dictionary <span className="text-muted-foreground font-normal">({items.length})</span></h3>
            {untranslated > 0 && <div className="text-xs text-[hsl(0,65%,55%)] mt-1">{untranslated} phrase{untranslated === 1 ? "" : "s"} still empty in Armenian</div>}
          </div>
          <div className="flex items-center gap-2">
            <Input placeholder="Search…" value={q} onChange={e => setQ(e.target.value)} className="w-64" data-testid="tr-search"/>
            <Button onClick={saveAll} disabled={!dirtyCount || saving} className="btn-pill bg-[hsl(12,65%,63%)] hover:bg-[hsl(12,70%,55%)]" data-testid="tr-save-all">
              <Save size={14} className="mr-2"/> {saving ? "Saving…" : dirtyCount ? `Save ${dirtyCount}` : "No changes"}
            </Button>
          </div>
        </div>

        {filtered.length === 0 ? (
          <div className="text-sm text-muted-foreground text-center py-10">
            {items.length === 0 ? "No saved translations yet — add one above." : "No matches for that search."}
          </div>
        ) : (
          <div className="space-y-2 max-h-[60vh] overflow-y-auto pr-1">
            {filtered.map(it => {
              const value = it.en in dirty ? dirty[it.en] : (it.hy || "");
              return (
                <div key={it.en} className="grid grid-cols-1 md:grid-cols-[1fr_1fr_40px] gap-2 items-center p-3 rounded-xl border border-border hover:bg-muted/30" data-testid={`tr-row-${it.en.slice(0,20)}`}>
                  <div className="text-sm font-medium truncate" title={it.en} data-no-translate>{it.en}</div>
                  <Input
                    value={value}
                    onChange={e => setHy(it.en, e.target.value)}
                    placeholder="Հայերեն"
                    className={value !== (it.hy || "") ? "border-[hsl(12,65%,63%)]" : ""}
                    data-testid={`tr-input-${it.en.slice(0,20)}`}
                  />
                  <button
                    onClick={() => remove(it.en)}
                    className="w-8 h-8 rounded-full text-muted-foreground hover:bg-[hsl(0,65%,55%)]/10 hover:text-[hsl(0,65%,55%)] flex items-center justify-center justify-self-end"
                    data-testid={`tr-del-${it.en.slice(0,20)}`}
                    title="Delete"
                  ><Trash2 size={14}/></button>
                </div>
              );
            })}
          </div>
        )}
      </Card>
    </div>
  );
}

function HomepageSettings() {
  const [settings, setSettings] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    api.get("/public/homepage-settings").then(r => setSettings(r.data)).catch(() => {});
  }, []);

  if (!settings) return <Card className="clay-card p-6 mt-4">Loading…</Card>;

  const updateFooter = (k, v) => setSettings(s => ({ ...s, footer: { ...s.footer, [k]: v } }));
  const move = (idx, dir) => {
    const order = [...settings.section_order];
    const j = idx + dir;
    if (j < 0 || j >= order.length) return;
    [order[idx], order[j]] = [order[j], order[idx]];
    setSettings({ ...settings, section_order: order });
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/homepage-settings", {
        footer: settings.footer,
        section_order: settings.section_order,
        pillars: settings.pillars || [],
      });
      toast.success("Homepage settings saved");
    } catch { toast.error("Failed to save"); }
    finally { setSaving(false); }
  };

  const pillars = settings.pillars || [];
  const updatePillar = (idx, k, v) => {
    const next = pillars.map((p, i) => i === idx ? { ...p, [k]: v } : p);
    setSettings({ ...settings, pillars: next });
  };
  const addPillar = () => setSettings({ ...settings, pillars: [...pillars, { icon: "Tent", title: "New card", title_hy: "", description: "Short description…", description_hy: "" }] });
  const removePillar = (idx) => setSettings({ ...settings, pillars: pillars.filter((_, i) => i !== idx) });
  const movePillar = (idx, dir) => {
    const arr = [...pillars];
    const j = idx + dir;
    if (j < 0 || j >= arr.length) return;
    [arr[idx], arr[j]] = [arr[j], arr[idx]];
    setSettings({ ...settings, pillars: arr });
  };

  const LABELS = {
    chapters: "Chapters", events: "Upcoming events", badges: "Badges",
    newsletters: "Newsletters", leaders: "Leaders", galleries: "Galleries", resources: "Resources",
  };

  return (
    <div className="grid md:grid-cols-2 gap-4 mt-4">
      <Card className="clay-card p-6" data-testid="admin-footer-editor">
        <div className="flex items-center gap-2 mb-4">
          <Globe size={16} className="text-[hsl(12,65%,55%)]"/>
          <h3 className="font-display font-bold text-lg">Footer & HQ info</h3>
        </div>
        <div className="space-y-3">
          <div>
            <Label>Description (English)</Label>
            <Textarea rows={3} value={settings.footer.description || ""} onChange={e => updateFooter("description", e.target.value)} data-testid="footer-desc-en"/>
          </div>
          <div>
            <Label>Description (Armenian)</Label>
            <Textarea rows={3} value={settings.footer.description_hy || ""} onChange={e => updateFooter("description_hy", e.target.value)} data-testid="footer-desc-hy"/>
          </div>
          <div>
            <Label>HQ address</Label>
            <Textarea rows={2} value={settings.footer.hq_address || ""} onChange={e => updateFooter("hq_address", e.target.value)} data-testid="footer-address"/>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Email</Label>
              <Input value={settings.footer.hq_email || ""} onChange={e => updateFooter("hq_email", e.target.value)} data-testid="footer-email"/>
            </div>
            <div>
              <Label>Phone</Label>
              <Input value={settings.footer.hq_phone || ""} onChange={e => updateFooter("hq_phone", e.target.value)} data-testid="footer-phone"/>
            </div>
          </div>
          <div>
            <Label className="flex items-center gap-1"><MapPin size={12}/> Map pin (latitude, longitude)</Label>
            <div className="grid grid-cols-2 gap-3">
              <Input type="number" step="any" value={settings.footer.latitude ?? ""} onChange={e => updateFooter("latitude", parseFloat(e.target.value))} data-testid="footer-lat"/>
              <Input type="number" step="any" value={settings.footer.longitude ?? ""} onChange={e => updateFooter("longitude", parseFloat(e.target.value))} data-testid="footer-lng"/>
            </div>
            <p className="text-xs text-muted-foreground mt-1">Yervand Kochar 17/6, Yerevan ≈ 40.1893, 44.5175</p>
          </div>

          <div className="border-t border-border pt-4 mt-2">
            <div className="font-semibold text-sm mb-2 flex items-center gap-2"><Globe size={14} className="text-[hsl(12,65%,55%)]"/> Social links <span className="text-xs text-muted-foreground font-normal">(leave empty to hide)</span></div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Facebook URL</Label>
                <Input value={settings.footer.facebook || ""} onChange={e => updateFooter("facebook", e.target.value)} placeholder="https://facebook.com/homenetmenhask" data-testid="footer-facebook"/>
              </div>
              <div>
                <Label className="text-xs">Instagram URL</Label>
                <Input value={settings.footer.instagram || ""} onChange={e => updateFooter("instagram", e.target.value)} placeholder="https://instagram.com/homenetmenhask" data-testid="footer-instagram"/>
              </div>
              <div>
                <Label className="text-xs">X / Twitter URL <span className="text-muted-foreground">(optional)</span></Label>
                <Input value={settings.footer.x || ""} onChange={e => updateFooter("x", e.target.value)} placeholder="https://x.com/homenetmenhask" data-testid="footer-x"/>
              </div>
              <div>
                <Label className="text-xs">Telegram URL <span className="text-muted-foreground">(optional)</span></Label>
                <Input value={settings.footer.telegram || ""} onChange={e => updateFooter("telegram", e.target.value)} placeholder="https://t.me/homenetmenhask" data-testid="footer-telegram"/>
              </div>
            </div>
          </div>
        </div>
      </Card>

      <Card className="clay-card p-6" data-testid="admin-section-order">
        <div className="flex items-center gap-2 mb-4">
          <GripVertical size={16} className="text-[hsl(12,65%,55%)]"/>
          <h3 className="font-display font-bold text-lg">Homepage section order</h3>
        </div>
        <p className="text-xs text-muted-foreground mb-3">Reorder how sections appear to guests. Top = first.</p>
        <div className="space-y-2">
          {settings.section_order.map((k, i) => (
            <div key={k} className="flex items-center gap-3 p-3 rounded-xl border border-border" data-testid={`section-row-${k}`}>
              <GripVertical size={14} className="text-muted-foreground"/>
              <div className="flex-1 font-semibold text-sm">{LABELS[k] || k}</div>
              <div className="flex gap-1">
                <Button size="sm" variant="ghost" onClick={() => move(i, -1)} disabled={i === 0} className="h-7 w-7 p-0" data-testid={`section-up-${k}`}>↑</Button>
                <Button size="sm" variant="ghost" onClick={() => move(i, 1)} disabled={i === settings.section_order.length - 1} className="h-7 w-7 p-0" data-testid={`section-down-${k}`}>↓</Button>
              </div>
            </div>
          ))}
        </div>
      </Card>

      <Card className="clay-card p-6 md:col-span-2">
        <h3 className="font-display font-bold text-xl mb-1">About the movement — pillar cards</h3>
        <p className="text-xs text-muted-foreground mb-4">The three (or more) feature cards right under the hero. Icons are picked from Lucide; add bilingual title + description.</p>
        <div className="space-y-3">
          {pillars.map((p, i) => (
            <div key={i} className="p-4 rounded-xl border border-border bg-muted/30 space-y-2" data-testid={`pillar-row-${i}`}>
              <div className="flex items-center gap-2 flex-wrap">
                <div className="uppercase-label flex-1">Pillar {i + 1}</div>
                <div className="flex gap-1">
                  <Button size="sm" variant="ghost" onClick={() => movePillar(i, -1)} disabled={i === 0} className="h-7 w-7 p-0" data-testid={`pillar-up-${i}`}>↑</Button>
                  <Button size="sm" variant="ghost" onClick={() => movePillar(i, 1)} disabled={i === pillars.length - 1} className="h-7 w-7 p-0" data-testid={`pillar-down-${i}`}>↓</Button>
                  <Button size="sm" variant="ghost" onClick={() => removePillar(i)} className="h-7 w-7 p-0 text-[hsl(0,65%,55%)] hover:bg-[hsl(0,65%,55%)]/10" data-testid={`pillar-remove-${i}`}><Trash2 size={13}/></Button>
                </div>
              </div>
              <div className="grid md:grid-cols-3 gap-2">
                <div>
                  <Label className="text-[11px]">Icon</Label>
                  <Select value={p.icon || "Tent"} onValueChange={v => updatePillar(i, "icon", v)}>
                    <SelectTrigger className="h-9" data-testid={`pillar-icon-${i}`}><SelectValue/></SelectTrigger>
                    <SelectContent>
                      {["Tent","Compass","Heart","Mountain","Shield","Globe","Star","BookOpen","Flag","Zap","Trees","Gift","Award","Flame","Users","Building2"].map(n => (
                        <SelectItem key={n} value={n}>{n}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="md:col-span-2 grid grid-cols-2 gap-2">
                  <div>
                    <Label className="text-[11px]">Title (EN)</Label>
                    <Input value={p.title || ""} onChange={e => updatePillar(i, "title", e.target.value)} data-testid={`pillar-title-${i}`}/>
                  </div>
                  <div>
                    <Label className="text-[11px]">Title (ՀԱ)</Label>
                    <Input value={p.title_hy || ""} onChange={e => updatePillar(i, "title_hy", e.target.value)} data-testid={`pillar-title-hy-${i}`}/>
                  </div>
                </div>
              </div>
              <div className="grid md:grid-cols-2 gap-2">
                <div>
                  <Label className="text-[11px]">Description (EN)</Label>
                  <Textarea rows={2} value={p.description || ""} onChange={e => updatePillar(i, "description", e.target.value)} data-testid={`pillar-desc-${i}`}/>
                </div>
                <div>
                  <Label className="text-[11px]">Description (ՀԱ)</Label>
                  <Textarea rows={2} value={p.description_hy || ""} onChange={e => updatePillar(i, "description_hy", e.target.value)} data-testid={`pillar-desc-hy-${i}`}/>
                </div>
              </div>
            </div>
          ))}
          <Button variant="outline" className="btn-pill" onClick={addPillar} data-testid="pillar-add">
            <Plus size={14} className="mr-2"/> Add pillar card
          </Button>
        </div>
      </Card>

      <div className="md:col-span-2 flex justify-end">
        <Button onClick={save} disabled={saving} className="btn-pill bg-[hsl(149,40%,30%)] hover:bg-[hsl(149,40%,25%)]" data-testid="save-homepage-settings">
          <Save size={14} className="mr-2"/> {saving ? "Saving…" : "Save homepage settings"}
        </Button>
      </div>
    </div>
  );
}


const VOCAB_META = [
  { key: "ranks", label: "Ranks", help: "Scouting ranks (e.g. Cub, Scout, Rover). Shows up in each member's profile dropdown.", color: "hsl(12,65%,63%)" },
  { key: "positions", label: "Leadership positions", help: "Titles like Patrol Leader, Scout Leader. Selecting one on a member prompts an account-role upgrade.", color: "hsl(149,40%,30%)" },
  { key: "patrols", label: "Patrols", help: "Patrol names used across all sections.", color: "hsl(32,87%,55%)" },
  { key: "degrees", label: "Scout degrees", help: "Progress milestones awarded by the leader.", color: "hsl(260,40%,55%)" },
];

function VocabularyManager() {
  const [data, setData] = useState({ ranks: [], positions: [], patrols: [], degrees: [] });
  const [loading, setLoading] = useState(true);
  const [draft, setDraft] = useState({});   // per-key new input
  const [saving, setSaving] = useState("");

  const load = () => {
    setLoading(true);
    api.get("/vocab")
      .then(r => setData(prev => ({ ...prev, ...r.data })))
      .catch(() => toast.error("Failed to load vocabulary"))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const notifyUpdated = () => {
    try { window.dispatchEvent(new CustomEvent("vocab-updated")); } catch (err) { /* noop */ }
  };

  const persist = async (key, values) => {
    setSaving(key);
    try {
      const { data: resp } = await api.put("/vocab", { key, values });
      setData(prev => ({ ...prev, [key]: resp.values }));
      notifyUpdated();
      return true;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
      return false;
    } finally { setSaving(""); }
  };

  const addValue = async (key) => {
    const v = (draft[key] || "").trim();
    if (!v) return;
    const next = [...(data[key] || []), v];
    const ok = await persist(key, next);
    if (ok) {
      setDraft(d => ({ ...d, [key]: "" }));
      toast.success(`Added “${v}”`);
    }
  };

  const removeValue = async (key, v) => {
    if (!window.confirm(`Remove “${v}” from ${key}? Members already using it keep the value.`)) return;
    const next = (data[key] || []).filter(x => x !== v);
    const ok = await persist(key, next);
    if (ok) toast.success(`Removed “${v}”`);
  };

  const moveValue = async (key, idx, dir) => {
    const arr = [...(data[key] || [])];
    const j = idx + dir;
    if (j < 0 || j >= arr.length) return;
    [arr[idx], arr[j]] = [arr[j], arr[idx]];
    await persist(key, arr);
  };

  if (loading) return <Card className="clay-card p-6 mt-4">Loading…</Card>;

  return (
    <div className="grid md:grid-cols-2 gap-4 mt-4" data-testid="vocab-manager">
      {VOCAB_META.map(meta => {
        const values = data[meta.key] || [];
        return (
          <Card key={meta.key} className="clay-card p-6" data-testid={`vocab-card-${meta.key}`}>
            <div className="flex items-start gap-3 mb-3">
              <div className="w-10 h-10 rounded-2xl flex-shrink-0 flex items-center justify-center" style={{ background: `${meta.color}22`, color: meta.color }}>
                <Globe size={18}/>
              </div>
              <div className="min-w-0">
                <h3 className="font-display font-bold text-lg">{meta.label} <span className="text-muted-foreground font-normal text-sm">({values.length})</span></h3>
                <p className="text-xs text-muted-foreground">{meta.help}</p>
              </div>
            </div>

            <div className="flex gap-2 mb-3">
              <Input
                value={draft[meta.key] || ""}
                onChange={e => setDraft(d => ({ ...d, [meta.key]: e.target.value }))}
                onKeyDown={e => { if (e.key === "Enter") { e.preventDefault(); addValue(meta.key); } }}
                placeholder={`Add a ${meta.label.toLowerCase().replace(/s$/, "")}…`}
                data-testid={`vocab-input-${meta.key}`}
              />
              <Button onClick={() => addValue(meta.key)} disabled={saving === meta.key} className="btn-pill bg-[hsl(149,40%,30%)]" data-testid={`vocab-add-${meta.key}`}>
                <Plus size={14} className="mr-1"/> Add
              </Button>
            </div>

            {values.length === 0 ? (
              <div className="text-sm text-muted-foreground py-6 text-center">No entries yet.</div>
            ) : (
              <ul className="space-y-1.5">
                {values.map((v, i) => (
                  <li key={`${v}-${i}`} className="flex items-center gap-2 p-2 rounded-xl border border-border bg-muted/30" data-testid={`vocab-row-${meta.key}-${i}`}>
                    <span className="flex-1 text-sm font-medium truncate">{v}</span>
                    <button onClick={() => moveValue(meta.key, i, -1)} disabled={i === 0 || saving === meta.key} className="text-xs px-2 py-1 text-muted-foreground hover:text-foreground disabled:opacity-30" title="Move up">↑</button>
                    <button onClick={() => moveValue(meta.key, i, 1)} disabled={i === values.length - 1 || saving === meta.key} className="text-xs px-2 py-1 text-muted-foreground hover:text-foreground disabled:opacity-30" title="Move down">↓</button>
                    <button onClick={() => removeValue(meta.key, v)} disabled={saving === meta.key} className="text-[hsl(0,65%,55%)] hover:bg-[hsl(0,65%,55%)]/10 rounded-full p-1 disabled:opacity-30" data-testid={`vocab-remove-${meta.key}-${i}`} title="Remove">
                      <Trash2 size={14}/>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </Card>
        );
      })}
    </div>
  );
}
